/**
 * Foreground consumer protocol for Lot 4B push display.
 * A background/hidden admin tab must not suppress the system notification.
 * ACK means the offer was successfully handled by the notification controller —
 * rejected/failed/stale/coalesced-incomplete refreshes must not suppress.
 */

import {
  PROSPECT_PUSH_ACK_TYPE,
  PROSPECT_PUSH_CONSUMER_ACK_BUDGET_MS,
  PROSPECT_PUSH_OFFER_TYPE,
  shouldSuppressForegroundPushAlert,
} from './prospectWebPushDisplay.js';

export function isDocumentEligibleForPushForegroundAck({
  visibilityState,
  hasFocus,
} = {}) {
  if (visibilityState !== 'visible') return false;
  if (hasFocus === false) return false;
  return true;
}

export function isAdminSessionEligibleForPushForegroundAck({
  role,
  impersonating = false,
  authUid,
  mounted = true,
} = {}) {
  if (mounted === false) return false;
  return role === 'super_admin' && impersonating !== true && Boolean(authUid);
}

/**
 * Controller outcomes are non-throwing. Only applied:true without error counts.
 */
export function isSuccessfulNotificationRefreshOutcome(outcome) {
  if (!outcome || typeof outcome !== 'object') return false;
  if (outcome.error === true) return false;
  if (outcome.applied !== true) return false;
  return true;
}

/**
 * Run the unread (+ optional open-panel soft list) refresh required before ACK.
 * Honors coalesced soft refreshes by awaiting their cycle completion.
 */
export async function refreshNotificationsForPushOffer({
  refreshUnread,
  loadList,
  panelOpen = false,
} = {}) {
  const unread =
    typeof refreshUnread === 'function' ? await refreshUnread() : { applied: false };
  if (!isSuccessfulNotificationRefreshOutcome(unread)) {
    return {
      ok: false,
      handled: false,
      reason: unread?.error ? 'unread_error' : 'unread_not_applied',
      unread,
    };
  }

  if (!panelOpen) {
    return { ok: true, handled: true, unread };
  }

  if (typeof loadList !== 'function') {
    return { ok: false, handled: false, reason: 'list_unavailable', unread };
  }

  let list = await loadList({ soft: true });
  if (list?.reason === 'refresh-coalesced' && typeof list.waitUntilSettled === 'function') {
    list = await list.waitUntilSettled();
  }
  if (!isSuccessfulNotificationRefreshOutcome(list)) {
    return {
      ok: false,
      handled: false,
      reason: list?.error ? 'list_error' : 'list_not_applied',
      unread,
      list,
    };
  }
  return { ok: true, handled: true, unread, list };
}

/**
 * Validate an acknowledgement against the posting WindowClient (SW-side).
 */
export function acceptProspectPushAcknowledgementFromClient({
  message,
  requestId,
  sourceClient,
} = {}) {
  if (!message || message.type !== PROSPECT_PUSH_ACK_TYPE) return null;
  if (message.requestId !== requestId) return null;
  if (message.eligible !== true || message.handled !== true) return null;
  if (!sourceClient || sourceClient.type !== 'window') return null;
  if (sourceClient.visibilityState !== 'visible') return null;
  if (sourceClient.focused !== true) return null;
  return {
    requestId: message.requestId,
    eligible: true,
    handled: true,
    clientId: typeof sourceClient.id === 'string' ? sourceClient.id : null,
    focused: true,
    visibilityState: 'visible',
  };
}

/**
 * Admin page consumer: handle offer only when foreground + eligible, then ACK
 * only after handleOffer reports successful applied refresh.
 */
export async function handleProspectPushOfferAsAdminConsumer({
  offer,
  role,
  impersonating,
  authUid,
  documentState,
  getDocumentState,
  getSessionState,
  handleOffer,
  postAck,
} = {}) {
  if (!offer || offer.type !== PROSPECT_PUSH_OFFER_TYPE) {
    return { acknowledged: false, reason: 'not_offer' };
  }

  const readDoc = () =>
    typeof getDocumentState === 'function' ? getDocumentState() : documentState || {};
  const readSession = () =>
    typeof getSessionState === 'function'
      ? getSessionState()
      : { role, impersonating, authUid, mounted: true };

  const initialDoc = readDoc();
  if (
    !isDocumentEligibleForPushForegroundAck({
      visibilityState: initialDoc?.visibilityState,
      hasFocus: initialDoc?.hasFocus,
    })
  ) {
    return { acknowledged: false, reason: 'not_foreground' };
  }
  const initialSession = readSession();
  if (
    !isAdminSessionEligibleForPushForegroundAck({
      role: initialSession?.role,
      impersonating: initialSession?.impersonating,
      authUid: initialSession?.authUid,
      mounted: initialSession?.mounted,
    })
  ) {
    return { acknowledged: false, reason: 'not_eligible_session' };
  }

  let handleResult = { ok: true, handled: true };
  if (typeof handleOffer === 'function') {
    try {
      const raw = await handleOffer({
        requestId: offer.requestId,
        ref: offer.ref,
        lang: offer.lang,
      });
      // Explicit controller outcomes only — undefined/void must not suppress.
      if (!raw || typeof raw !== 'object') {
        return { acknowledged: false, reason: 'handle_not_successful' };
      }
      handleResult = raw;
    } catch {
      return { acknowledged: false, reason: 'handle_failed' };
    }
  }

  if (handleResult.ok !== true || handleResult.handled !== true) {
    return {
      acknowledged: false,
      reason: handleResult.reason || 'handle_not_successful',
      handleResult,
    };
  }

  // Re-check live foreground + eligibility after handling.
  const afterDoc = readDoc();
  if (
    !isDocumentEligibleForPushForegroundAck({
      visibilityState: afterDoc?.visibilityState,
      hasFocus: afterDoc?.hasFocus,
    })
  ) {
    return { acknowledged: false, reason: 'lost_foreground' };
  }
  const afterSession = readSession();
  if (
    !isAdminSessionEligibleForPushForegroundAck({
      role: afterSession?.role,
      impersonating: afterSession?.impersonating,
      authUid: afterSession?.authUid,
      mounted: afterSession?.mounted,
    })
  ) {
    return { acknowledged: false, reason: 'lost_eligibility' };
  }

  if (typeof postAck === 'function') {
    postAck({
      type: PROSPECT_PUSH_ACK_TYPE,
      requestId: offer.requestId,
      eligible: true,
      handled: true,
    });
  }
  return { acknowledged: true, reason: null, handleResult };
}

/**
 * SW push orchestration with injected client/message I/O (testable handler chain).
 */
export async function runProspectPushDisplayDecision({
  requestId,
  offerToClients,
  waitForAcks,
  budgetMs = PROSPECT_PUSH_CONSUMER_ACK_BUDGET_MS,
  acceptAck = acceptProspectPushAcknowledgementFromClient,
} = {}) {
  const acknowledgements = [];
  const recordMessage = (messageEvent) => {
    const accepted = acceptAck({
      message: messageEvent?.data,
      requestId,
      sourceClient: messageEvent?.source,
    });
    if (accepted) acknowledgements.push(accepted);
  };

  if (typeof offerToClients === 'function') {
    await offerToClients({ requestId });
  }
  const rawAcks =
    typeof waitForAcks === 'function'
      ? await waitForAcks({ requestId, budgetMs, recordMessage })
      : acknowledgements.slice();
  const list = Array.isArray(rawAcks) ? rawAcks : acknowledgements.slice();
  const suppress = shouldSuppressForegroundPushAlert({
    acknowledgements: list,
    requestId,
  });
  return { suppress, acknowledgements: list };
}
